import { useEffect, useMemo, useState } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  parseWorkspaceName,
  type DatabricksWorkspaceSelection,
} from '../DatabricksWorkspaceField/DatabricksWorkspaceField';

export interface ExistingCatalogSelection {
  workspace: string;
  catalog: string;
  schemas: string[];
}

const SCHEMA_PATTERN = /^[a-z][a-z0-9_]{1,62}$/;

export const ExistingCatalogSchemasField = ({
  onChange,
  formData,
  formContext,
}: FieldExtensionComponentProps<ExistingCatalogSelection[]>) => {
  const useExistingCatalog =
    formContext?.formData?.add_existing_catalog === true;
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const selection = formContext?.formData?.workspace as
    | DatabricksWorkspaceSelection
    | undefined;
  const workspaceNames = selection?.workspaces ?? [];
  const workspaceKey = workspaceNames.join('\u0000');
  const workspaces = useMemo(
    () => (workspaceKey ? workspaceKey.split('\u0000') : []),
    [workspaceKey],
  );
  const [catalogsByWorkspace, setCatalogsByWorkspace] = useState<
    Record<string, string[]>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [schemaDrafts, setSchemaDrafts] = useState<Record<string, string>>({});
  const [schemaErrors, setSchemaErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    if (!useExistingCatalog || !workspaceKey) {
      setCatalogsByWorkspace({});
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setError(undefined);
    (async () => {
      try {
        const azureBase = await discoveryApi.getBaseUrl('azure');
        const response = await fetchApi.fetch(
          `${azureBase}/databricks/catalogs?workspaces=${encodeURIComponent(
            JSON.stringify(workspaces),
          )}`,
        );
        if (!response.ok) throw new Error(String(response.status));
        const body = (await response.json()) as {
          catalogs?: Record<string, string[]>;
        };
        if (!cancelled) setCatalogsByWorkspace(body.catalogs ?? {});
      } catch {
        if (!cancelled) setError('Failed to load catalogs for the selected workspaces.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, useExistingCatalog, workspaceKey, workspaces]);

  const values = formData ?? [];
  const update = (workspace: string, patch: Partial<ExistingCatalogSelection>) => {
    const next = workspaces.map(name => {
      const current = values.find(value => value.workspace === name) ?? {
        workspace: name,
        catalog: '',
        schemas: [],
      };
      return name === workspace ? { ...current, ...patch } : current;
    });
    onChange(next);
  };

  const addSchema = (workspace: string, schema: string | undefined) => {
    const trimmed = schema?.trim() ?? '';
    if (!trimmed || !SCHEMA_PATTERN.test(trimmed)) {
      setSchemaErrors(current => ({
        ...current,
        [workspace]: 'Use lowercase letters, numbers, and underscores.',
      }));
      return;
    }
    const value = values.find(item => item.workspace === workspace);
    if (value?.schemas.some(existing => existing.toLowerCase() === trimmed.toLowerCase())) {
      setSchemaErrors(current => ({
        ...current,
        [workspace]: `Schema "${trimmed}" has already been added.`,
      }));
      return;
    }
    setSchemaErrors(current => ({ ...current, [workspace]: '' }));
    update(workspace, {
      schemas: value?.schemas.includes(trimmed)
        ? value.schemas
        : [...(value?.schemas ?? []), trimmed],
    });
  };

  if (!useExistingCatalog) {
    return null;
  }

  return (
    <Box>
      {loading && <CircularProgress size={20} />}
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
      {workspaces.map(workspace => {
        const value = values.find(item => item.workspace === workspace) ?? {
          workspace,
          catalog: '',
          schemas: [],
        };
        const parsed = parseWorkspaceName(workspace);
        return (
          <Box key={workspace} mb={3}>
            <Typography variant="subtitle1">
              {parsed?.spoke ?? workspace} / {parsed?.environment ?? ''}
            </Typography>
            <Autocomplete
              options={catalogsByWorkspace[workspace] ?? []}
              value={value.catalog || null}
              onChange={(_, catalog) => update(workspace, { catalog: catalog ?? '' })}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Existing catalog"
                  required
                  error={!value.catalog}
                  helperText={!value.catalog ? 'Select a catalog.' : undefined}
                  margin="normal"
                />
              )}
            />
            <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
              <TextField
                fullWidth
                label="Schema name"
                placeholder="bronze"
                margin="normal"
                value={schemaDrafts[workspace] ?? ''}
                error={
                  Boolean(schemaDrafts[workspace]) &&
                  (!SCHEMA_PATTERN.test(schemaDrafts[workspace]) ||
                    Boolean(schemaErrors[workspace]))
                }
                helperText={
                  schemaErrors[workspace] ||
                  'Use lowercase letters, numbers, and underscores.'
                }
                onChange={event =>
                  setSchemaDrafts(current => ({
                    ...current,
                    [workspace]: event.target.value,
                  }))
                }
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addSchema(workspace, schemaDrafts[workspace]);
                    setSchemaDrafts(current => ({
                      ...current,
                      [workspace]: '',
                    }));
                  }
                }}
              />
              <Button
                type="button"
                variant="outlined"
                color="primary"
                size="small"
                style={{ marginTop: 16 }}
                onClick={() => {
                  addSchema(workspace, schemaDrafts[workspace]);
                  setSchemaDrafts(current => ({
                    ...current,
                    [workspace]: '',
                  }));
                }}
              >
                Add
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
              {value.schemas.map(schema => (
                <Chip
                  key={schema}
                  label={schema}
                  onDelete={() =>
                    update(workspace, {
                      schemas: value.schemas.filter(item => item !== schema),
                    })
                  }
                />
              ))}
            </Box>
            {!value.schemas.length && (
              <Typography color="error" variant="caption">
                At least one schema is required.
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
